import useForwardedRef from "@bedrock-layout/use-forwarded-ref"
import {
	CSSProperties,
	forwardRef,
	useLayoutEffect,
	useRef,
	useState,
} from "react"
import { findDOMNode } from "react-dom"

interface ResponsiveImageProps {
	url: string
	style?: CSSProperties
	padding?: number
	onMouseEnter?: () => void
	onMouseLeave?: () => void
}

const ResponsiveImage = forwardRef<HTMLDivElement, ResponsiveImageProps>(
	({ url, style, padding = 0, onMouseEnter, onMouseLeave }, ref) => {
		const container = useForwardedRef(ref)

		const [width, setWidth] = useState<number | undefined>(undefined)
		const [height, setHeight] = useState<number | undefined>(undefined)
		const [aspectRatio, setAspectRatio] = useState<number | undefined>(
			undefined
		)

		const containerParent = useRef<HTMLElement | undefined>(undefined)
		const image = useRef<HTMLImageElement>(null)

		const onLoad = () => {
			if (image.current) {
				setAspectRatio(image.current.naturalHeight / image.current.naturalWidth)
			}
		}

		if (container && container.current) {
			const node = findDOMNode(container.current)
			if (node && node.parentElement) {
				containerParent.current = node.parentElement
			}
		}

		if (containerParent.current && !width) {
			setWidth(containerParent.current.offsetWidth - padding)
			setHeight(containerParent.current.offsetHeight - padding)
		}

		useLayoutEffect(() => {
			const updateSize = () => {
				if (containerParent.current) {
					setWidth(containerParent.current.offsetWidth - padding)
					setHeight(containerParent.current.offsetHeight - padding)
				}
			}

			window.addEventListener("resize", updateSize)

			updateSize()

			return () => {
				window.removeEventListener("resize", updateSize)
			}
		}, [padding])

		const isPortrait = (aspectRatio ?? 0) > 1

		let _width = "100%"
		if (
			width &&
			aspectRatio &&
			height &&
			(isPortrait || width * aspectRatio > height)
		) {
			_width = Math.min((1 / aspectRatio) * height, width) + "px"
		}

		let _height = "100%"
		if (
			width &&
			aspectRatio &&
			height &&
			(!isPortrait || height * aspectRatio > width)
		) {
			_height = Math.min(aspectRatio * width, height) + "px"
		}

		return (
			<div
				className="flex"
				ref={container}
				style={{
					maxHeight: "calc(100vh - 20px)",
					width: _width,
					height: _height,
					...style,
				}}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				<img
					alt="responsive"
					className="block w-full h-full"
					height="auto"
					ref={image}
					src={url}
					style={{
						display: aspectRatio ? "block" : "none",
					}}
					width="auto"
					onLoad={onLoad}
				/>
			</div>
		)
	}
)

export default ResponsiveImage
